<template>
  <div data-testid="file-attachment">
    <div data-testid="file-attachment__header" class="d-flex align-center justify-space-between mb-2">
      <h4 data-testid="file-attachment__title" class="black--text font-weight-regular">Uploaded Documents</h4>
      <v-btn
        data-testid="file-attachment__download-all-btn"
        v-if="hasFiles"
        @click="downloadAll"
        class="pa-0 cursor-pointer"
        text
      >
        <v-icon data-testid="file-attachment__download-all-icon">mdi-download</v-icon>
        <span>Download All</span>
      </v-btn>
    </div>
    <v-list data-testid="file-attachment__list" dense class="radius-small pa-0 d-flex align-center gap-3 file-list">
      <template v-if="displayFiles.length > 0">
        <v-list-item
          data-testid="file-attachment__item"
          v-for="(file, index) in displayFiles"
          :key="index"
          class="d-flex align-center justify-space-between pa-4 rounded-lg border-on file-list-item"
        >
          <div data-testid="file-attachment__item-left" class="d-flex align-center">
            <v-icon data-testid="file-attachment__file-icon" class="mr-2" color="primary">mdi-file</v-icon>
            <div data-testid="file-attachment__item-meta" class="d-flex flex-column">
              <span data-testid="file-attachment__file-name" class="black--text font-size-md">{{ file.file_name || file.modifiedname }}</span>
              <span data-testid="file-attachment__file-uploaded-on" class="font-size-xm grey--text" v-if="file.uploaded_on">{{ $nova.formatDateTime(file.uploaded_on) }}</span>
            </div>
          </div>
          <div data-testid="file-attachment__item-actions">
            <v-btn data-testid="file-attachment__download-btn" icon class="wid-height" @click="$nova.downloadFile(file.file_id)">
              <v-icon data-testid="file-attachment__download-icon">mdi-download</v-icon>
            </v-btn>
            <v-btn
              data-testid="file-attachment__delete-btn"
              icon
              class="wid-height"
              :disabled="isDeleting(file)"
              @click="!isDeleting(file) && handleDeleteFile(file)"
            >
              <v-progress-circular
                data-testid="file-attachment__deleting-spinner"
                v-if="isDeleting(file)"
                indeterminate
                color="red"
                size="16"
                width="2"
              />
              <v-icon data-testid="file-attachment__delete-icon" v-else color="red">mdi-delete</v-icon>
            </v-btn>
          </div>
        </v-list-item>
      </template>
      <template v-else>
        <v-list-item data-testid="file-attachment__empty-state" class="justify-center">
          <span data-testid="file-attachment__empty-text" class="grey--text">No files attached</span>
        </v-list-item>
      </template>
    </v-list>
   <div class="">
     <div data-testid="file-attachment__remarks" class="remarks mt-3">
      <div data-testid="file-attachment__remarks-header" class="remarks__header section-heading pb-2"> Preparer's Remarks</div>
      <template v-if="task?.remark">
        <div data-testid="file-attachment__remarks-body" class="remarks__body elevation-1">
          <div data-testid="file-attachment__remarks-text" class="remarks__text" :title="task.remark" v-html="highlightHashtags(task.remark)"></div>
        </div>
      </template>
      <div data-testid="file-attachment__remarks-empty" v-else class="remarks__empty">No remarks added</div>
    </div>
    <div data-testid="file-attachment__remarks" class="remarks mt-3" v-if="task?.approver_remark">
      <div data-testid="file-attachment__remarks-header" class="remarks__header section-heading pb-2">Approver's Remarks</div>
      <template v-if="task?.approver_remark">
        <div data-testid="file-attachment__remarks-body" class="remarks__body elevation-1">
          <div data-testid="file-attachment__remarks-text" class="remarks__text" :title="task.approver_remark" v-html="highlightHashtags(task.approver_remark)"></div>
        </div>
      </template>
      <div data-testid="file-attachment__remarks-empty" v-else class="remarks__empty">No remarks added</div>
    </div>
   </div>
  
    
    
    
  </div>
</template>

<script>
import { extractFileIdsFromTask, hasDownloadableFiles } from '../../../../utils/utils';

export default {
  name: 'FileAttachment',
  props: {
    task: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      deleteFileIds: [] // track file_ids currently being deleted
    };
  },
  computed: {
    /**
     * Returns list of files to display, preferring upload_v2 but falling back to upload
     * For display purposes, we need the full file objects (not just IDs)
     */
    displayFiles() {
      // Prefer upload_v2 (has file metadata like file_name, uploaded_on)
      if (Array.isArray(this.task?.upload_v2) && this.task.upload_v2.length > 0) {
        return this.task.upload_v2;
      }
      // Fallback to upload - convert IDs to minimal file objects for display
      const fileIds = extractFileIdsFromTask(this.task);
      if (fileIds.length > 0) {
        return fileIds.map(id => ({ file_id: id, file_name: id }));
      }
      return [];
    },
    /**
     * Check if there are any downloadable files
     */
    hasFiles() {
      return hasDownloadableFiles(this.task);
    }
  },
  methods: {
    hasDownloadableFiles,
    extractFileIdsFromTask,
    highlightHashtags(text) {
      if (!text) return '';
      // Replace any word starting with # with a red-colored span
      return text.replace(/(#\w+)/g, '<span style="color: red; font-weight: 500;">$1</span>');
    },
    downloadAll() {
      // Use utility function to extract file IDs with backward compatibility
      const ids = extractFileIdsFromTask(this.task);
      if (ids.length > 0 && this.$nova && typeof this.$nova.downloadFiles === 'function') {
        this.$nova.downloadFiles(ids);
      } else {
        this.$store?.commit?.('sbError', 'No files available to download');
      }
    },
    isDeleting(file) {
      const fid = file?.file_id || file?._id;
      return this.deleteFileIds.includes(fid);
    },
    copyRemark() {
      const txt = this.task?.remark || '';
      if (!txt) return;
      try {
        if (this.$nova && typeof this.$nova.copy === 'function') {
          this.$nova.copy(txt);
        } else if (navigator?.clipboard?.writeText) {
          navigator.clipboard.writeText(txt);
        }
        this.$store && this.$store.commit && this.$store.commit('sbSuccess', 'Remark copied');
      } catch (e) {
        this.$store && this.$store.commit && this.$store.commit('sbError', 'Copy failed');
      }
    },
    async handleDeleteFile(file) {
      try {
        const fid = file?.file_id || file?._id;
        if (fid && !this.deleteFileIds.includes(fid)) {
          this.deleteFileIds.push(fid);
        }
        const result = await this.$store.dispatch('tasks/deleteFileFromTask', { file, task: this.task });
        if (result && result.success) {
          // Create updated task object without mutating the prop
          const updatedTask = result.updatedTask || { ...this.task };
          
          // Remove from upload_v2
          if (Array.isArray(updatedTask.upload_v2)) {
            updatedTask.upload_v2 = updatedTask.upload_v2.filter(
              f => (f.file_id || f._id) !== (file.file_id || file._id)
            );
          }
          
          // Sync legacy task.upload (array of ids) if present
          if (Array.isArray(updatedTask.upload)) {
            const removeId = file.file_id || file._id;
            updatedTask.upload = updatedTask.upload.filter(id => id !== removeId);
          }
          
          this.$emit('delete-file', file);
          this.$emit('file-uploaded', updatedTask);
        }
      } catch (err) {
        // Error already handled in store
      } finally {
        const fid = file?.file_id || file?._id;
        if (fid) {
          this.deleteFileIds = this.deleteFileIds.filter(id => id !== fid);
        }
      }
    }
  }
}
</script>
<style scoped>
.width-33{
  width: 33%;
}
.file-list {
  gap: 10px;
  width: 100%;
  display: flex;
  flex-wrap: wrap;
}
.file-list-item {
  max-width: 33%;
  flex: 1 1 30%;
  box-sizing: border-box;
}
.wid-height{
  width: auto;
  height: auto;
}
.section-heading{
  font-size:16px !important;
  line-height:1.3;
}
.remarks__body { border:1px solid #e3e6ea; border-radius:6px; padding:10px 12px; display:flex; flex-direction:column; gap:6px; width: max-content; }
.remarks__text { font-size:13px; line-height:1.4; white-space:pre-wrap; word-break:break-word; }
.remarks__empty { font-size:12px; color:#999; font-style:italic; padding:4px 0 0 0; }
</style>
